import { Footer, Header, PageHeader, Sidebar } from '@components/partials';
import { Loader } from '@cloudclevr/components';
import useUIStore from '@store/uiStore';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '@store/authStore';
import { HttpClient } from '@api/HttpClient';
import {
  CurrentUserResponse,
  OnboardingModalStatus,
  UserRoles,
} from '@hooks/users/types';
import { API_ENDPOINTS } from '@api/ApiEndpoints';
import { HTTP_CONFIG_API_URL } from '@common/constants';

import { Variant } from '@components/ui/Toast';
import useServicesStore from '@store/servicesStore';
import { ServiceIntergrationResponse } from '@hooks/connectedServices/types';
import { useHttpClient } from '@hooks/utils/httpConfig';
import { PageHeaderProvider } from '../../../context/PageHeaderContext';
import { useAuth } from '@auth';
import { TermsAcceptancePage } from '@pages/Authentication/TermsAcceptance';

export const BaseLayout = () => {
  const { isOverlayFixed, addToast } = useUIStore();
  const authContext = useAuth();
  const authStore = useAuthStore();
  const servicesStore = useServicesStore();
  const [isLoading, setIsLoading] = useState(!authStore.isDataFetched);
  const location = useLocation();



  const navigate = useNavigate();
  const showToast = (message: string, variant: Variant) => {
    addToast({
      variant: variant,
      message: message,
      closeable: true,
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const usersClient = new HttpClient(
    HTTP_CONFIG_API_URL.organization,
    showToast,
    () => authContext
  );
  const connectedServicesClient = useHttpClient();

  useEffect(() => {
    const handleTokenAcquisition = async () => {
      try {
        // If data already fetched, don't show loader
        if (authStore.isDataFetched) {
          setIsLoading(false);
          return;
        }
        
        if (authContext.isAuthenticated && !authStore.isDataFetched) {
          setIsLoading(true);
          
          // Token is already managed by AuthProvider
          authStore.setToken(authContext.token || '');
          authStore.setIsAuthenticated(authContext.isAuthenticated);

          const [currentUserResponse, termsResponse] = await Promise.all([
            usersClient.get<CurrentUserResponse>(API_ENDPOINTS.CURRENT_USER),
            usersClient.get<boolean>(
              API_ENDPOINTS.TERMS_CONDITIONS_HAS_GRANTED
            ),
          ]);

          if (termsResponse || currentUserResponse.role === UserRoles.SUPPORT) {
            await usersClient
              .get<OnboardingModalStatus>(
                `${API_ENDPOINTS.USER_ONBOARDING_MODAL}`
              )
              .then((response: any) => {
                authStore.setModalsStatus(response.modalsStatus);
              });

            await connectedServicesClient
              .get<ServiceIntergrationResponse[]>(
                `${API_ENDPOINTS.SERVICE_INTEGRATIONS}`
              )
              .then((response: ServiceIntergrationResponse[]) => {
                servicesStore.setServiceIntegrations(response);
                servicesStore.setHelixStatus(response?.find((item) =>
                  item.vendor === 2)?.status);
                servicesStore.SetIsFetched(true);
              });
          }
          authStore.setTermsAndConditions(termsResponse);
          authStore.setUser(currentUserResponse);
          authStore.setUserRole(currentUserResponse.role as UserRoles);
          authStore.setIsDataFetched(true);
          setIsLoading(false);
        } else if (!authContext.isAuthenticated) {
          setIsLoading(false);
        }
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    };

    handleTokenAcquisition();
  }, [authContext.isAuthenticated, authStore.isDataFetched]);

  useEffect(() => {
    if (authContext.isAuthenticated && authContext.isInitialized) {
      // If org T&Cs are not accepted, show T&C modal to first Super Admin only (handled in modal logic)
      // Once T&Cs are accepted, all users go directly to dashboard
      // No more /select-profile or insight selection
      // No action needed here; routing is now handled by T&C modal and dashboard default
    }
  }, [
    authContext.isAuthenticated,
    authContext.isInitialized,
    authStore.role,
    authStore.isDataFetched,
    authStore.isTermsAndCondtitionsGranted,
  ]);

  if (
    isLoading ||
    !authContext.isAuthenticated ||
    !authContext.isInitialized
  ) {
    return <Loader />;
  }

  const showContent = authStore.isTermsAndCondtitionsGranted || location.pathname === '/terms-and-conditions';

  return !isOverlayFixed && !isLoading ? (
      <PageHeaderProvider>
        {showContent ? <Outlet /> : <TermsAcceptancePage />}
      </PageHeaderProvider>
  ) : (
    !isLoading && (
      <PageHeaderProvider>
        {showContent ? (
          <div
            id="content"
            className="with-sidebar relative block w-full bg-interfaceColor-5 p-0 px-0 pb-0 pt-0"
          >
            <Sidebar />

            <main
              className={cn(
                'ml-0 flex min-h-screen flex-col justify-between xl:min-h-[calc(100vh-35px)]'
              )}
            >
              <Header />
              <div className="p-4 lg:p-5 xl:pt-[80px]">
                <PageHeader />
                <Outlet />
              </div>
              <div className="flex w-full pl-0 md:pl-10">
                <Footer />
              </div>
            </main>
          </div>
        ) : <TermsAcceptancePage />}
      </PageHeaderProvider>
    )
  );
};
